import React, { useEffect, useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import useAuth, { useAuthEffects } from '../components/Auth';
import { Alert, Box, Button, Dialog, DialogTitle, DialogContent, TextField, Grid2, Typography, MenuItem, Select, FormControl, InputLabel, DialogActions, Switch, FormControlLabel, Snackbar, Tooltip, Tabs, Tab, Avatar, Paper, Divider, Collapse } from '@mui/material';
import { useAppSelector } from '../hooks';
import { selectCompanyTools, selectUserProfile, selectMyCompany } from './accountSlice';
import { useUpdateMyCompanyMutation } from '../services/accounts';
import { t } from 'i18next';
import { useGetCompanyToolsQuery, useRemoveCompanyToolMutation } from '../services/tools';
import { ToolShedV2 } from '../components/ToolShed';
import { SimpleList } from '../components/SimpleList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteCompanyShippingMethodMutation, useGetCompanyShippingMethodsQuery, useAddCompanyShippingMethodMutation, useUploadCompanyImageMutation, useUploadCompanyCoverImageMutation } from '../services/companies';
import { useActivateVendorProfileMutation, useDeactivateVendorProfileMutation, useGetCompanyVendorProfileQuery, useUpdateVendorProfileMutation } from '../services/vendorProfile';
import FileDropzone from '../components/FileDropZone';
import PersonIcon from '@mui/icons-material/Person';
import LandscapeIcon from '@mui/icons-material/Landscape';
import { useGetAllShippingRegionsQuery, useGetCompanyShippingRegionsQuery, useBulkUpdateCompanyShippingRegionsMutation } from '../services/shippingRegions';
import { ShippingMethodRegionsSelector } from '../components/ShippingMethodRegionsSelector';
import { Currencies, CURRENCY_SYMBOLS } from '../types';
import { useNavigate } from 'react-router-dom';
import slugify from 'slugify';

interface TabPanelProps {
	children?: React.ReactNode;
	tabId: string;
	value: string;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, tabId, ...other } = props;
	return (
		<Box
			flex={1}
			// overflow='auto'
			role="tabpanel"
			hidden={value !== tabId}
			id={`simple-tabpanel-${tabId}`}
			aria-labelledby={`simple-tab-${tabId}`}
			{...other}
		>
			{children}
		</Box>
	);
}

const SHIPPING_OPTION_METHODS = ['delivery', 'pickup'];

const ProfileView = () => {
	useAuthEffects();
	const myCompany = useAppSelector(selectMyCompany);
	const myAccount = useAppSelector(selectUserProfile); // TODO: rename to selectMyAccount for clarify?
	const [updateMyCompanyMutation, {error: updateMyCompanyError}] = useUpdateMyCompanyMutation();
	const companyTools = useAppSelector(selectCompanyTools);
	const {userProfile, isAuthLoading} = useAuth();
	useGetCompanyToolsQuery(undefined, {skip: !userProfile});
	const [removeCompanyTool] = useRemoveCompanyToolMutation();
	const [addCompanyShippingMethod, {isSuccess: isAddCompanyShippingMethodSuccess}] = useAddCompanyShippingMethodMutation();
	const [isAddDeliveryOptionDialogOpen, setIsAddDeliveryOptionDialogOpen] = useState(false);
	const [deleteCompanyShippingMethod, {isSuccess: isDeleteCompanyShippingMethodSuccess}] = useDeleteCompanyShippingMethodMutation();
	const [, {isSuccess: isActivateVendorProfileSuccess}] = useActivateVendorProfileMutation();
	const [deactivateVendorProfile, {isSuccess: isDeactivateVendorProfileSuccess}] = useDeactivateVendorProfileMutation();
	const [updateVendorProfile, {isSuccess: isUpdateVendorProfileSuccess}] = useUpdateVendorProfileMutation();
	const [uploadCompanyImage] = useUploadCompanyImageMutation();
	const [uploadCompanyCoverImage] = useUploadCompanyCoverImageMutation();
	const {data: allShippingRegions = []} = useGetAllShippingRegionsQuery();
	const {data: companyShippingRegions = [], refetch: refetchCompanyShippingRegions} = useGetCompanyShippingRegionsQuery(undefined, {skip: !myCompany?.id});
	const [bulkUpdateCompanyShippingRegions, {isSuccess: isBulkUpdateCompanyShippingRegionsSuccess}] = useBulkUpdateCompanyShippingRegionsMutation();
	const {data: companyVendorProfile} = useGetCompanyVendorProfileQuery(undefined, {skip: !userProfile});
	const {data: companyShippingMethods = [], refetch: refetchCompanyShippingMethods} = useGetCompanyShippingMethodsQuery(undefined, {skip: !myCompany?.roleVendor});
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [tabValue, setTabValue] = useState<'account' | 'vendor' | 'other'>('account');
	const [isRoleVendorDisableConfirmDialogOpen, setIsRoleVendorDisableConfirmDialogOpen] = useState(false);
	const [profileId, setProfileId] = useState<string>('');
	const [isProfileIdFocused, setIsProfileIdFocused] = useState(false);
	const navigate = useNavigate();
	const currencySymbol = CURRENCY_SYMBOLS[companyVendorProfile?.currency as Currencies];

	useEffect(() => {
		if (!userProfile && !isAuthLoading) {
			navigate('/');
		}
	}, [isAuthLoading, myCompany]);

	useEffect(() => {
		if (myCompany?.profileId) {
			setProfileId(myCompany.profileId);
		}
	}, [myCompany?.profileId]);
	
	useEffect(() => {
		if (isUpdateVendorProfileSuccess || isActivateVendorProfileSuccess || isDeactivateVendorProfileSuccess) {
			setSuccessMessage('Changes saved.');
		}
	}, [isUpdateVendorProfileSuccess, isActivateVendorProfileSuccess, isDeactivateVendorProfileSuccess]);

	useEffect(() => {
		if (isAddCompanyShippingMethodSuccess || isDeleteCompanyShippingMethodSuccess) {
			refetchCompanyShippingMethods();
		}
	}, [isAddCompanyShippingMethodSuccess, isDeleteCompanyShippingMethodSuccess]);

	useEffect(() => {
		if (isBulkUpdateCompanyShippingRegionsSuccess) {
			refetchCompanyShippingRegions();
		}
	}, [isBulkUpdateCompanyShippingRegionsSuccess, refetchCompanyShippingRegions]);

	useEffect(() => {
		if (updateMyCompanyError) {
			setErrorMessage((updateMyCompanyError as any).data?.message);
		}
	}, [updateMyCompanyError]);

	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		if (formData) {
			updateMyCompanyMutation({
				name: formData.get('name') as string,
				profileId: formData.get('profileId') ? formData.get('profileId') as string : null,
				bio: formData.get('bio') as string,
			}).unwrap().then(() => {
				setSuccessMessage('Changes saved.');
			}).catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update profile');
			});
		}
	};

	const handleVendorProfileFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		if (formData) {
			updateVendorProfile({
				id: companyVendorProfile?.id,
				description: formData.get('description') as string,
				address: formData.get('address') as string,
				legalName: formData.get('legalName') as string,
				taxId: formData.get('taxId') ? formData.get('taxId') as string : null,
				receiveOrdersEmail: formData.get('receiveOrdersEmail') as string,
				website: formData.get('website') as string,
				vendorContactEmail: formData.get('vendorContactEmail') as string,
				currency: formData.get('currency') as Currencies,
			});
		}
	};

	const handleAddDeliveryOption = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const shippingMethod = new FormData(e.target as HTMLFormElement);
		addCompanyShippingMethod({
			method: shippingMethod.get('method') as string,
			variant: shippingMethod.get('variant') as string,
			price: parseFloat(shippingMethod.get('price') as string),
			description: shippingMethod.get('description') as string,
			shippingRegions: [],
		}).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to add shipping method');
		});
		setIsAddDeliveryOptionDialogOpen(false);
	};

	const handleDeleteDeliveryOption = (id: number) => {
		deleteCompanyShippingMethod(id).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to delete shipping method');
		});
	};

	const handleRoleCustomerChange = () => {
		updateMyCompanyMutation({...myCompany, roleCustomer: !myCompany?.roleCustomer}).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to update customer profile');
		});
	};

	const handleRoleVendorChange = () => {
		if (!myCompany?.roleVendor) {
			// TODO: redirect to vendor setup page
			// activateVendorProfile().unwrap().catch((error) => {
			// 	setErrorMessage((error as any)?.data?.message ?? 'Failed to activate vendor profile');
			// });
		} else {
			setIsRoleVendorDisableConfirmDialogOpen(false);
			deactivateVendorProfile().unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to deactivate vendor profile');
			});
		}
	};

	const handleRoleCreatorChange = () => {
		updateMyCompanyMutation({...myCompany, roleCreator: !myCompany?.roleCreator});
	};

	const handleCompanyCoverImageUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		uploadCompanyCoverImage(formData).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to upload image');
		});
	};

	const handleCompanyImageUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		uploadCompanyImage(formData).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to upload image');
		});
	};

	const handleBulkUpdateShippingRegions = (addCountries: string[], removeCountries: string[], shippingMethodId: number) => {
		if (myCompany?.id) {
			bulkUpdateCompanyShippingRegions({ addCountries, removeCountries, shippingMethodId }).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update shipping regions');
			});
		}
	};

	const handleProfileIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setProfileId(slugify(e.target.value, {lower: true, strict: true, locale: 'en', trim: false}));
	};

	return <PageWrapper isLoading={!myCompany} maxWidth='sm'>
		{myCompany?.roleVendor && <Tabs
			value={tabValue}
			onChange={(e, value) => setTabValue(value)}
			variant='fullWidth'
		>
			<Tab label='General' value='account' />
			{myCompany?.roleVendor && <Tab label='Vendor' value='vendor' />}
		</Tabs>}

		<CustomTabPanel value={tabValue} tabId='account'>
			<Box display='flex' flexDirection='column' gap={4} py={2}>
				<Box display='flex' flexDirection='column' alignItems='center' py={2} gap={1}>
					<Typography variant='h6'>Profile image</Typography>
					<Avatar sx={{width: 150, height: 150, mb: 1}} src={myCompany?.imgUrl ?? ''}>
						<PersonIcon sx={{fontSize: 100}} />
					</Avatar>
					<FileDropzone onFileUpload={handleCompanyImageUpload} id='file-upload-image' accept='image/*' />
				</Box>
				<Box display='flex' flexDirection='column' alignItems='center' py={2} gap={1}>
					<Typography variant='h6'>Cover image</Typography>
					<Avatar variant='square' sx={{width: '100%', height: 200}} src={myCompany?.coverImgUrl ?? ''}>
						<LandscapeIcon sx={{fontSize: 100}} />
					</Avatar>
					<FileDropzone onFileUpload={handleCompanyCoverImageUpload} id='file-upload-cover-image' accept='image/*' />
				</Box>
				{myCompany && <form onSubmit={handleFormSubmit}>
					<Typography variant='h6'>Profile information</Typography>
					<Box display='flex' flexDirection='column' gap={1}>
						<TextField required name='name' label='Name' defaultValue={myCompany?.name} disabled={false} variant='filled' fullWidth autoComplete='off' />
						{(myCompany?.roleVendor || myCompany?.roleCreator) && <div>
							<TextField required name='profileId' label='Profile ID' disabled={false} variant='filled' fullWidth onChange={handleProfileIdChange} value={profileId} onFocus={() => setIsProfileIdFocused(true)} onBlur={() => setIsProfileIdFocused(false)} autoComplete='off' />
							<Collapse in={Boolean(profileId) || isProfileIdFocused}>
								<Typography variant='body2' textAlign='center' pt={0.5} pb={2}>
									https://www.diyfeed.com/author/<span style={{fontWeight: 600}}>{slugify(profileId, {lower: true, strict: true, locale: 'en', trim: true}) || 'my-creative-page'}</span>
								</Typography>
							</Collapse>
						</div>}
						<TextField name='bio' label='Bio' defaultValue={myCompany?.bio} disabled={false} variant='filled' fullWidth multiline autoComplete='off' />

						<Box display='flex' flexDirection='row' gap={1} justifyContent='space-between'>
							<Button type='submit' variant='contained'>Save changes</Button>
							<Tooltip title='Please notify us at support@diyfeed.com to deactivate your account'>
								<div style={{display: 'inline-block'}}>
									<Button variant='text' color='error' disabled={true}>Deactivate account</Button>
								</div>
							</Tooltip>
						</Box>
					</Box>
					<Snackbar open={Boolean(successMessage)} autoHideDuration={6000} onClose={() => {
						setSuccessMessage(null);
					}}><Alert severity='success'>{successMessage}</Alert></Snackbar>
					<Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => {
						setErrorMessage(null);
					}}><Alert severity='error'>{errorMessage}</Alert></Snackbar>
				</form>}
				<div>
					<Typography variant='h6'>Signed in as</Typography>
					{myAccount && (<Box mt={1}>
						<TextField name='email' label='E-mail address' defaultValue={myAccount?.email} disabled variant='filled' fullWidth autoComplete='off' />
					</Box>)}
				</div>
				<Box>
					<Typography variant='h6'>Configure profile</Typography>
					<Box mt={2} display='flex' flexDirection='column'>
						<FormControlLabel
							sx={{userSelect: 'none'}}
							slotProps={{
								typography: {
									sx: {
										ml: 1,
										mt: -0.25,
										fontSize: '1.5rem',
									},
								},
							}}
							control={<Switch 
								name='roleCustomer'
								checked={myCompany?.roleCustomer}
								// disabled={true}
								onChange={() => handleRoleCustomerChange()} />} 
							label='Customer profile'
						/>
						<Typography variant='body2'>
							Save plans to favourites and view your purchase history.
						</Typography>
					</Box>
					<Box mt={2} display='flex' flexDirection='column'>
						<Box display='flex' flexDirection='row'>
							<FormControlLabel
								sx={{userSelect: 'none'}}
								slotProps={{
									typography: {
										sx: {
											ml: 1,
											mt: -0.25,
											fontSize: '1.5rem',
										},
									},
								}}
								control={<Switch 
									name='roleVendor'
									checked={myCompany?.roleVendor}
									// disabled={true}
									// onChange={() => myCompany?.roleVendor ? setIsRoleVendorDisableConfirmDialogOpen(true) : handleRoleVendorChange()}
									onChange={() => myCompany?.roleVendor ? setIsRoleVendorDisableConfirmDialogOpen(true) : navigate('/setup-account/vendor')}
								/>} 
								label='Vendor profile'
							/>
						</Box>
						<Typography variant='body2'>
							Advertise your company, publish shopping lists, process orders and more.
						</Typography>
					</Box>
					<Box mt={2} display='flex' flexDirection='column'>
						<Box display='flex' flexDirection='row'>
							<FormControlLabel
								sx={{userSelect: 'none'}}
								slotProps={{
									typography: {
										sx: {
											ml: 1,
											mt: -0.25,
											fontSize: '1.5rem',
										},
									}
								}}
								control={<Switch 
									name='roleCreator'
									checked={myCompany?.roleCreator}
									// disabled={true}
									onChange={() => handleRoleCreatorChange()} />} 
								label='Creator profile' 
							/>
						</Box>
						<Typography variant='body2'>
							Publish your DIY content on the platform.
						</Typography>
					</Box>
				</Box>
			</Box>
		</CustomTabPanel>
		<CustomTabPanel value={tabValue} tabId='vendor'>
			<Box display='flex' flexDirection='column' my={2} gap={1}>
				<Typography variant='h6'>Vendor information</Typography>
				{companyVendorProfile && <form onSubmit={handleVendorProfileFormSubmit}>
					<Grid2 container spacing={1}>
						<TextField name='legalName' label='Legal name' defaultValue={companyVendorProfile?.legalName} disabled={false} variant='filled' fullWidth required autoComplete='off' />
						<TextField name='address' label='Legal address' defaultValue={companyVendorProfile?.address} disabled={false} variant='filled' fullWidth autoComplete='off' required />
						<TextField name='taxId' label='VAT number' defaultValue={companyVendorProfile?.taxId} disabled={false} variant='filled' fullWidth autoComplete='off' />
						{/* <TextField name='website' label='Website' defaultValue={companyVendorProfile?.website} disabled={false} variant='filled' fullWidth autoComplete='off' /> */}
						<TextField name='receiveOrdersEmail' label='Receive orders email' defaultValue={companyVendorProfile?.receiveOrdersEmail} disabled={false} variant='filled' fullWidth autoComplete='off' required />
						<TextField name='vendorContactEmail' label='Vendor contact email' defaultValue={companyVendorProfile?.vendorContactEmail} variant='filled' fullWidth autoComplete='off' />
						<Grid2 size={12}>
							<FormControl fullWidth>
								<InputLabel id={`${String('currency')}-label`}>Currency</InputLabel>
								<Select
									labelId={`${String('currency')}-label`}
									id={`${String('currency')}-select`}
									name='currency'
									label='Currency'
									disabled={false}
									variant='filled'
									defaultValue={companyVendorProfile?.currency}
								>
									{[
										{ label: Currencies.EUR, value: Currencies.EUR },
										{ label: Currencies.USD, value: Currencies.USD },
									].map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid2>
						<Grid2 size={12} sx={{userSelect: 'none'}}>
							<Button type='submit' variant='contained'>Save changes</Button>
						</Grid2>
					</Grid2>
				</form>}
			</Box>
			{/* Shipping regions are now managed per shipping method */}
			{myCompany?.roleVendor && <Box py={2}>
				<Typography variant='h6'>{t('profileEditView.shippingMethods')}</Typography>
				<SimpleList
					items={companyShippingMethods.map((shippingMethod) => ({
						label: shippingMethod.variant ? `${t(`shippingMethods.${shippingMethod.method}`)} - ${shippingMethod.variant}` : t(`shippingMethods.${shippingMethod.method}`),
						secondary: shippingMethod.description ?? undefined,
						properties: [{
							label: shippingMethod.price ? `${currencySymbol}${shippingMethod.price}` : t('shippingMethodsView.noCharge'),
						}],
						actions: [{
							icon: <DeleteIcon />,
							onClick: () => {
								handleDeleteDeliveryOption(shippingMethod.id);
							},
						}],
						additionalInfo: <Box>
							<Divider />
							<Paper elevation={0} sx={{borderTopLeftRadius: 0, borderTopRightRadius: 0, p: 2, pt: 1}}>
								<ShippingMethodRegionsSelector
									allRegions={allShippingRegions}
									selectedRegions={companyShippingRegions.filter((region) => region.shippingMethodId === shippingMethod.id)}
									shippingMethodId={shippingMethod.id}
									companyId={myCompany?.id || 0}
									onBulkUpdate={(addCountries: string[], removeCountries: string[]) => 
										handleBulkUpdateShippingRegions(addCountries, removeCountries, shippingMethod.id)
									}
								/>
							</Paper>
						</Box>,
						sx: {
							borderBottomLeftRadius: 0,
							borderBottomRightRadius: 0, 
						},
					}))}
					actions={[
						{
							label: t('shippingMethodsView.addShippingMethod'),
							icon: <AddIcon />,
							onClick: () => {
								setIsAddDeliveryOptionDialogOpen(true);
							},
						},
					]}
				/>
			</Box>}
		</CustomTabPanel>
		<CustomTabPanel value={tabValue} tabId='customer'>
			<Box>
				<ToolShedV2
					title={t('profileEditView.myToolshed')} 
					tools={companyTools ?? []} 
					sx={{mt: 4, minHeight: 230}} 
					menuItemsFn={(tool) => [
						{
							label: t('toolShed.removeTool'),
							icon: <DeleteIcon />,
							onClick: () => removeCompanyTool(tool.id),
						},
					]}
				/>
				<Typography variant='body2'>
						Here you can manage which tools you have access to.
				</Typography>
			</Box>
		</CustomTabPanel>
		<Dialog open={isAddDeliveryOptionDialogOpen} onClose={() => setIsAddDeliveryOptionDialogOpen(false)}>
			<form onSubmit={handleAddDeliveryOption}>
				<DialogTitle>Add shipping option</DialogTitle>
				<DialogContent sx={{overflow: 'visible'}}>
					<Grid2 container spacing={1}>
						<Grid2 size={12}>
							<FormControl fullWidth>
								<InputLabel>{t('shippingMethodsView.method')}</InputLabel>
								<Select name='method' label={t('shippingMethodsView.method')} defaultValue={SHIPPING_OPTION_METHODS[0]}>
									{SHIPPING_OPTION_METHODS.map((method) => <MenuItem key={method} value={method}>{t(`shippingMethods.${method}`)}</MenuItem>)}
								</Select>
							</FormControl>
						</Grid2>
						<Grid2 size={12}>
							<TextField name='variant' label={t('shippingMethodsView.variant')} fullWidth autoComplete='off' placeholder={t('shippingMethodsView.variantPlaceholder')} />
						</Grid2>
						<Grid2 size={12}>
							<TextField name='price' type='number' label={t('shippingMethodsView.price')} fullWidth inputProps={{ step: 0.01 }} autoComplete='off' placeholder='Leave blank for no charge' />
						</Grid2>
						<Grid2 size={12}>
							<TextField name='description' label={t('shippingMethodsView.additionalInfo')} fullWidth multiline autoComplete='off' />
						</Grid2>
					</Grid2>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsAddDeliveryOptionDialogOpen(false)}>{t('common.cancel')}</Button>
					<Button type='submit'>{t('common.save')}</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Dialog open={isRoleVendorDisableConfirmDialogOpen} onClose={() => setIsRoleVendorDisableConfirmDialogOpen(false)}>
			<DialogTitle sx={{backgroundColor: 'error.main', color: 'white'}}>Delete vendor profile?</DialogTitle>
			<DialogContent>
				<Typography mt={2}>This action erases information that is critical for processing any future orders, effectively stopping your products from being visible to the customers.</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setIsRoleVendorDisableConfirmDialogOpen(false)}>{t('common.cancel')}</Button>
				<Button type='submit' variant='text' color='error' onClick={() => handleRoleVendorChange()}>I understand</Button>
			</DialogActions>
		</Dialog>
		<Snackbar
			open={Boolean(successMessage)}
			autoHideDuration={6000}
			onClose={() => {
				setSuccessMessage(null);
			}}
		><Alert severity='success'>{successMessage}</Alert></Snackbar>
		<Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => {
			setErrorMessage(null);
		}}><Alert severity='error'>{errorMessage}</Alert></Snackbar>
	</PageWrapper>;
};

export default ProfileView;
